import { cn } from '@/lib/cn';
import type { Viewport } from '@personalizehq/schemas/ir';
import { Moon, Sun } from 'lucide-react';

// The toggles flip the RENDERER's own token resolution and solver inputs — this is not a preview
// mode, it is the same code path the device runs. `font_scale` is the differentiator (a 1.5× system
// font is where the customer bug reports come from, and neither competitor shows it at all), so it
// sits in the toolbar rather than behind a menu: an author should trip over it.
//
// The scales and the RTL cell are the conformance matrix's (`packages/schemas/src/viewports.ts`);
// the device list is local, because VIEWPORTS is one size by design — it varies scheme/scale/dir.

const DEVICES = [
  { label: '390 × 844', w: 390, h: 844 },
  { label: '375 × 667', w: 375, h: 667 },
  { label: '768 × 1024', w: 768, h: 1024 },
] as const;

const SCALES = [1, 1.5, 2] as const;

interface ViewportToolbarProps {
  viewport: Viewport;
  onChange: (viewport: Viewport) => void;
}

export function ViewportToolbar({ viewport, onChange }: ViewportToolbarProps): React.JSX.Element {
  const set = (patch: Partial<Viewport>): void => onChange({ ...viewport, ...patch });

  return (
    <div className="flex items-center gap-4 text-[12px]">
      <Group>
        {DEVICES.map((device) => (
          <Toggle
            key={device.label}
            active={viewport.w === device.w && viewport.h === device.h}
            onClick={() => set({ w: device.w, h: device.h })}
          >
            {device.label}
          </Toggle>
        ))}
      </Group>

      <Group>
        <Toggle
          active={viewport.color_scheme === 'light'}
          aria-label="Light"
          onClick={() => set({ color_scheme: 'light' })}
        >
          <Sun className="size-3.5" />
        </Toggle>
        <Toggle
          active={viewport.color_scheme === 'dark'}
          aria-label="Dark"
          onClick={() => set({ color_scheme: 'dark' })}
        >
          <Moon className="size-3.5" />
        </Toggle>
      </Group>

      <Group>
        {SCALES.map((scale) => (
          <Toggle
            key={scale}
            active={viewport.font_scale === scale}
            aria-label={`Font scale ${scale.toFixed(1)}`}
            onClick={() => set({ font_scale: scale })}
          >
            {scale.toFixed(1)}×
          </Toggle>
        ))}
      </Group>

      <Group>
        {(['ltr', 'rtl'] as const).map((direction) => (
          <Toggle
            key={direction}
            active={(viewport.direction ?? 'ltr') === direction}
            onClick={() => set({ direction })}
          >
            {direction.toUpperCase()}
          </Toggle>
        ))}
      </Group>
    </div>
  );
}

function Group({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="flex items-center gap-0.5 rounded-lg border p-0.5">{children}</div>;
}

function Toggle({
  active,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active: boolean }): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        'flex h-6 items-center gap-1 rounded-md px-2 transition-colors',
        active ? 'bg-secondary font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'
      )}
      {...props}
    >
      {children}
    </button>
  );
}
